// PrinterService.js - Handles all Bluetooth printing operations
import {notify} from "helpers/globalFunctions";
import html2canvas from "html2canvas";

export class PrinterService {
    constructor() {
        this.printState = {
            printCharacteristic: null,
            server: null,
            index: 0,
            data: null,
            canvas: null,
            isTransmitting: false,
            operationId: null,
        };
    }

    async connectToBluetooth() {
        if (!navigator.bluetooth) {
            notify.error({description: "Bluetooth not supported on this device/browser."});
            return false;
        }

        try {
            const device = await navigator.bluetooth.requestDevice({
                filters: [{services: ["000018f0-0000-1000-8000-00805f9b34fb"]}],
            });

            console.log("> Found " + device.name);
            const server = await device.gatt.connect();
            this.printState.server = server;

            const service = await server.getPrimaryService("000018f0-0000-1000-8000-00805f9b34fb");
            const characteristic = await service.getCharacteristic("00002af1-0000-1000-8000-00805f9b34fb");

            this.printState.printCharacteristic = characteristic;
            notify.success({description: "Connected successfully !!"});
            return true;
        } catch (error) {
            console.error("Bluetooth connection error:", error);
            this.printState.printCharacteristic = null;
            return false;
        }
    }

    isConnected() {
        return this.printState.printCharacteristic && this.printState.server && this.printState.server.connected;
    }

    isBusy() {
        return this.printState.isTransmitting || this.printState.data !== null;
    }

    getDarkPixel(imageData, x, y) {
        const red = imageData[(this.printState.canvas.width * y + x) * 4];
        const green = imageData[(this.printState.canvas.width * y + x) * 4 + 1];
        const blue = imageData[(this.printState.canvas.width * y + x) * 4 + 2];
        const alpha = imageData[(this.printState.canvas.width * y + x) * 4 + 3];

        if (alpha < 10) return 0;
        return red + green + blue > 730 ? 0 : 1;
    }

    getImagePrintData(imageData) {
        if (!imageData) return new Uint8Array([]);

        const printData = new Uint8Array((this.printState.canvas.width / 8) * this.printState.canvas.height + 8);
        let offset = 0;

        // ESC/POS header
        printData[0] = 29;
        printData[1] = 118;
        printData[2] = 48;
        printData[3] = 0;
        printData[4] = this.printState.canvas.width / 8;
        printData[5] = 0;
        printData[6] = this.printState.canvas.height % 256;
        printData[7] = this.printState.canvas.height / 256;
        offset = 7;

        for (let i = 0; i < this.printState.canvas.height; ++i) {
            for (let k = 0; k < this.printState.canvas.width / 8; ++k) {
                const k8 = k * 8;
                printData[++offset] =
                    this.getDarkPixel(imageData, k8 + 0, i) * 128 +
                    this.getDarkPixel(imageData, k8 + 1, i) * 64 +
                    this.getDarkPixel(imageData, k8 + 2, i) * 32 +
                    this.getDarkPixel(imageData, k8 + 3, i) * 16 +
                    this.getDarkPixel(imageData, k8 + 4, i) * 8 +
                    this.getDarkPixel(imageData, k8 + 5, i) * 4 +
                    this.getDarkPixel(imageData, k8 + 6, i) * 2 +
                    this.getDarkPixel(imageData, k8 + 7, i);
            }
        }
        return printData;
    }

    async sendImageDataBatch(operationId) {
        const CHUNK_SIZE = 128; // Smaller chunks for better reliability
        const DELAY_MS = 150; // Longer delay to prevent buffer overflow
        const MAX_RETRIES = 3;
        const BUFFER_CHECK_INTERVAL = 10; // Check every 10 chunks

        let chunkCount = 0;

        while (this.printState.index < this.printState.data.length) {
            // Verify operation is still valid
            if (this.printState.operationId !== operationId) {
                throw new Error("Operation cancelled");
            }

            const nextIndex = Math.min(this.printState.index + CHUNK_SIZE, this.printState.data.length);
            const chunk = this.printState.data.slice(this.printState.index, nextIndex);
            const progress = Math.round((this.printState.index / this.printState.data.length) * 100);

            let retryCount = 0;
            let success = false;

            while (!success && retryCount < MAX_RETRIES) {
                try {
                    await this.printState.printCharacteristic.writeValueWithResponse(chunk);
                    success = true;

                    if (chunkCount % 20 === 0) {
                        // Log every 20 chunks
                        console.log(
                            `[${operationId}] Progress: ${progress}% (${this.printState.index}/${this.printState.data.length})`
                        );
                    }
                } catch (err) {
                    retryCount++;
                    console.warn(`[${operationId}] Chunk ${chunkCount} failed (attempt ${retryCount}):`, err.message);

                    if (retryCount >= MAX_RETRIES) {
                        console.error(`[${operationId}] Failed after ${MAX_RETRIES} attempts at chunk ${chunkCount}`);
                        throw err;
                    }

                    // Exponential backoff with longer delays
                    await new Promise((resolve) => setTimeout(resolve, DELAY_MS * retryCount * 2));
                }
            }

            this.printState.index = nextIndex;
            chunkCount++;

            // Buffer management: longer delay every N chunks
            let delay = DELAY_MS;
            if (chunkCount % BUFFER_CHECK_INTERVAL === 0) {
                delay = DELAY_MS * 3; // Triple delay for buffer recovery
                console.log(`[${operationId}] Buffer check delay at chunk ${chunkCount}`);
            }

            await new Promise((resolve) => setTimeout(resolve, delay));
        }

        console.log(`[${operationId}] Batch transmission completed. Total chunks: ${chunkCount}`);
    }

    async printSlip(printElementId, slipGenerator = null) {
        const operationId = Date.now() + Math.random();

        // 🔥 MUST BE FIRST STEP — hard reset EVERYTHING
        await this.hardResetPrintEnvironment();
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (this.isBusy()) {
            throw new Error("Print operation already in progress");
        }
        
        // Extra buffer drain time for late slips (larger data)
        if (this.isConnected()) {
            await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // CRITICAL: Complete cleanup before starting
        await this.forceCleanup();
        await new Promise((resolve) => setTimeout(resolve, 300)); // Extra delay after cleanup

        this.printState.operationId = operationId;
        this.printState.isTransmitting = true;

        try {
            const printNode = document.getElementById(printElementId);
            if (!printNode) {
                throw new Error("Print element not found");
            }

            // CRITICAL: DOM reset MUST happen FIRST, before any canvas operations
            if (slipGenerator) {
                await slipGenerator.hardResetSlipDOM();
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            // Send printer reset and clear buffer AFTER DOM reset
            await this.resetPrinter();
            await new Promise((resolve) => setTimeout(resolve, 300));

            // Force DOM reflow after reset
            void printNode.offsetHeight;
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Create fresh canvas with enhanced options
            const canvas = await html2canvas(printNode, {
                width: printNode.offsetWidth,
                height: printNode.offsetHeight + 100,
                scale: 1,
                useCORS: true,
                allowTaint: false,
                removeContainer: true,
                backgroundColor: "#ffffff",
                logging: false,
            });

            // Verify canvas creation
            if (!canvas || canvas.width === 0 || canvas.height === 0) {
                throw new Error("Invalid canvas generated");
            }

            this.printState.canvas = canvas;
            const context = canvas.getContext("2d");
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;

            // Generate fresh print data with validation
            this.printState.index = 0;
            this.printState.data = this.getImagePrintData(imageData);

            if (!this.printState.data || this.printState.data.length === 0) {
                throw new Error("No print data generated");
            }

            console.log(`[${operationId}] Starting transmission: ${this.printState.data.length} bytes`);

            // Skip actual transmission if no printer connected (for testing)
            if (!this.isConnected()) {
                console.log(`[${operationId}] No printer connected - skipping transmission for testing`);
                return;
            }

            await this.sendImageDataBatch(operationId);

            // Verify operation still valid before final commands
            if (this.printState.operationId !== operationId) {
                throw new Error("Operation cancelled during transmission");
            }

            // Send feed and cut commands with verification
            // Feed before cutting
            await new Promise((resolve) => setTimeout(resolve, 400));
            await this.printState.printCharacteristic.writeValueWithResponse(
                new Uint8Array([0x1b, 0x64, 0x03]) // Line feed (3 lines)
            );
            // SHORT DELAY BEFORE CUT
            await new Promise((resolve) => setTimeout(resolve, 200));

            // ✅ Correct Full-Cut Command for Shreyans SRS58301
            await this.printState.printCharacteristic.writeValueWithResponse(new Uint8Array([0x1d, 0x56, 0x00]));

            // Wait to allow motor to complete the cut
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // CRITICAL: Clear printer buffer after print
            await this.printState.printCharacteristic.writeValueWithResponse(new Uint8Array([0x18])); // CAN
            await new Promise((resolve) => setTimeout(resolve, 200));
            
            // Reset printer to default state
            await this.printState.printCharacteristic.writeValueWithResponse(new Uint8Array([0x1b, 0x40])); // ESC @
            await new Promise((resolve) => setTimeout(resolve, 500));
            
            // Extra drain time for large prints (late slips)
            if (this.printState.data.length > 5000) {
                await new Promise((resolve) => setTimeout(resolve, 500));
            }

            console.log(`[${operationId}] Print completed successfully`);
        } catch (error) {
            console.error(`[${operationId}] Print error:`, error);
            await this.forceCleanup();
            throw error;
        } finally {
            // Enhanced cleanup with verification
            if (this.printState.operationId === operationId) {
                await this.completeCleanup(operationId);
            }
        }
    }

    async hardResetPrintEnvironment() {
        console.log("Hard resetting print environment...");

        // 1. Cancel any ongoing print
        this.printState.operationId = null;
        this.printState.isTransmitting = false;

        // 2. Clear canvas completely
        if (this.printState.canvas) {
            try {
                const ctx = this.printState.canvas.getContext("2d");
                ctx && ctx.clearRect(0, 0, this.printState.canvas.width, this.printState.canvas.height);
            } catch (e) {}
        }
        this.printState.canvas = null;

        // 3. Clear data buffer
        this.printState.data = null;
        this.printState.index = 0;

        // 4. Remove ALL existing canvases from DOM
        document.querySelectorAll("canvas").forEach((c) => {
            try {
                c.width = 0;
                c.height = 0;
                c.remove();
            } catch (e) {}
        });

        // 5. Force clean print content area (but don't replace the node)
        const printNode = document.getElementById("print-content");
        if (printNode) {
            printNode.innerHTML = "";
        }

        // 6. Real memory flush
        await new Promise((r) => setTimeout(r, 100));
        if (window.gc) window.gc();

        console.log("Hard reset complete.");
    }

    async resetPrinter() {
        try {
            if (this.printState.printCharacteristic) {
                // Send ESC/POS reset command
                await this.printState.printCharacteristic.writeValueWithResponse(new Uint8Array([0x1b, 0x40])); // ESC @
                await new Promise((resolve) => setTimeout(resolve, 200));
                console.log("Printer reset command sent");
            }
        } catch (error) {
            console.warn("Printer reset failed:", error);
        }
    }

    async forceCleanup() {
        console.log("Force cleanup initiated");

        // Cancel any ongoing operation
        this.printState.operationId = null;
        this.printState.isTransmitting = false;

        // Clear canvas with proper disposal
        if (this.printState.canvas) {
            const ctx = this.printState.canvas.getContext("2d");
            if (ctx) {
                ctx.clearRect(0, 0, this.printState.canvas.width, this.printState.canvas.height);
            }
            this.printState.canvas.width = 0;
            this.printState.canvas.height = 0;
            this.printState.canvas = null;
        }

        // Clear data buffer
        if (this.printState.data) {
            this.printState.data = null;
        }
        this.printState.index = 0;

        // Send printer buffer clear if connected
        try {
            if (this.isConnected()) {
                await this.printState.printCharacteristic.writeValueWithResponse(new Uint8Array([0x18])); // CAN (Cancel)
                await new Promise((resolve) => setTimeout(resolve, 100));
            }
        } catch (error) {
            console.warn("Buffer clear failed:", error);
        }

        // Force garbage collection hint
        if (window.gc) {
            window.gc();
        }
    }

    async completeCleanup(operationId) {
        console.log(`[${operationId}] Complete cleanup initiated`);

        // Wait for printer to finish processing
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Clear all state
        this.printState.canvas = null;
        this.printState.data = null;
        this.printState.index = 0;
        this.printState.isTransmitting = false;
        this.printState.operationId = null;

        console.log(`[${operationId}] Complete cleanup finished`);
    }

}
